import React from 'react';
import { providerApi, type ProviderPayoutListReq, type ProviderPayoutVo, type Page } from 'api/provider';

type UseProviderPayoutHistoryListParams = {
    timeRangeMonths: number;
    current?: number;
    size?: number;
    sortBy?: ProviderPayoutListReq['sortBy'];
    orderType?: ProviderPayoutListReq['orderType'];
    isPractice?: boolean;
};

const useProviderPayoutHistoryList = (
    params: UseProviderPayoutHistoryListParams,
): [ProviderPayoutVo[] | undefined, number, boolean, (next?: Partial<ProviderPayoutListReq>) => Promise<void>] => {
    const {
        timeRangeMonths,
        current = 1,
        size = 10,
        sortBy = 'PAYOUT_DATE',
        orderType = 'DESC',
        isPractice = false,
    } = params;

    const [records, setRecords] = React.useState<ProviderPayoutVo[]>();
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState(false);

    const fetchData = React.useCallback(
        async (next: Partial<ProviderPayoutListReq> = {}) => {
            try {
                setLoading(true);
                const list = isPractice ? providerApi.billing.practice.payout.list : providerApi.billing.payout.list;
                const res = await list({
                    current,
                    size,
                    sortBy,
                    orderType,
                    timeRangeMonths,
                    ...next,
                });

                if (!res?.error) {
                    const page = res.data?.data as Page<ProviderPayoutVo> | undefined;
                    setRecords(page?.records || []);
                    setTotal(page?.total || 0);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        },
        [current, size, timeRangeMonths, sortBy, orderType, isPractice],
    );

    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    return [records, total, loading, fetchData];
};

export default useProviderPayoutHistoryList;
